import {
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useItemTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { useMemo } from 'react';
import { r, s } from '../../theme/tokens';
import { uiTokens } from '../../theme/uiTokens';
import { Box } from './Box';
import { getChartTooltipSx } from './chartTooltipStyles';
import { useTheme } from './hooks';
import { Text } from './Text';
import { MuiPieChart } from './xCharts';
import type { MuiPieChartProps } from './xCharts';

export type PieChartDatum = { name: string; value: number; color?: string };

type PieChartProps = {
  data: PieChartDatum[];
  height?: number;
  innerRadius?: number;
  outerRadius?: number | string;
  showLegend?: boolean;
};

const CHART_MARGIN_NONE = { left: 0, right: 0, top: 0, bottom: 0 } as const;
const PIE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    trigger: 'item',
  },
} as const satisfies NonNullable<MuiPieChartProps['slotProps']>;
const pieChartPalette = [
  uiTokens.statusColors.purple,
  uiTokens.statusColors.green,
  uiTokens.statusColors.orange,
  uiTokens.statusColors.blue,
  uiTokens.statusColors.red,
] as const;

type PieTooltipContentProps = ChartsTooltipProps & {
  totalsByLabel: ReadonlyMap<string, number>;
  total: number;
};

const formatPercentage = (value: number): string => value.toFixed(2).replace(/\.?0+$/, '');

const PieTooltipContent = ({
  totalsByLabel,
  total,
  classes,
  sx,
  ...props
}: PieTooltipContentProps): JSX.Element | null => {
  const theme = useTheme();
  const tooltipData = useItemTooltip<'pie'>();
  if (!tooltipData) return null;
  const rawValue =
    typeof tooltipData.value === 'number' ? tooltipData.value : Number(tooltipData.value);
  const value = Number.isFinite(rawValue)
    ? rawValue
    : tooltipData.label
      ? (totalsByLabel.get(tooltipData.label) ?? 0)
      : 0;
  const percentage = total > 0 ? formatPercentage((value / total) * 100) : '0';

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <Box
        className={classes?.paper}
        sx={{
          borderRadius: r('md'),
          border: `1px solid ${theme.app.colors.border}`,
          boxShadow: 'none',
          backgroundColor: theme.app.colors.surface,
          color: theme.app.colors.text,
          pt: '0px',
          pb: '4px',
          pl: '2px',
          pr: '2px',
        }}
      >
        <Box
          component="table"
          className={classes?.table}
          sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <Box component="tbody">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  display: 'table-cell',
                  alignItems: 'center',
                  p: 0,
                  pl: '5px',
                  pr: '5px',
                  pt: '6px',
                  pb: '4px',
                  border: 0,
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                  fontWeight: theme.typography.fontWeightRegular,
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  verticalAlign: 'middle',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: s('md'),
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: s('lg'),
                      height: s('lg'),
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: tooltipData.color,
                    }}
                  />
                  {tooltipData.label ?? ''}
                </Box>
              </Box>
              <Box
                component="td"
                sx={{
                  p: 0,
                  pl: '5px',
                  pr: '5px',
                  pt: '6px',
                  pb: '4px',
                  border: 0,
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                  fontWeight: theme.typography.fontWeightRegular,
                  whiteSpace: 'nowrap',
                  color: theme.app.colors.text,
                  textAlign: 'right',
                  verticalAlign: 'middle',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {value}
              </Box>
              <Box
                component="td"
                sx={{
                  p: 0,
                  pl: '5px',
                  pr: '5px',
                  pt: '6px',
                  pb: '4px',
                  border: 0,
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                  fontWeight: theme.typography.fontWeightRegular,
                  whiteSpace: 'nowrap',
                  color: theme.app.colors.text,
                  textAlign: 'right',
                  verticalAlign: 'middle',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {`(${percentage}%)`}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ChartsTooltipContainer>
  );
};

export const PieChart = ({
  data,
  height = 220,
  innerRadius = 54,
  outerRadius = '80%',
  showLegend = true,
}: PieChartProps): JSX.Element => {
  const theme = useTheme();
  const series = useMemo(
    () =>
      data.map((entry, index) => ({
        id: entry.name,
        label: entry.name,
        value: entry.value,
        color: entry.color ?? pieChartPalette[index % pieChartPalette.length],
      })),
    [data],
  );
  const chartSeries = useMemo(
    () => [{ data: series, innerRadius, outerRadius }],
    [innerRadius, outerRadius, series],
  );
  const total = useMemo(() => data.reduce((sum, entry) => sum + entry.value, 0), [data]);
  const totalsByLabel = useMemo(
    () => new Map(data.map((entry) => [entry.name, entry.value])),
    [data],
  );
  const tooltipSlot = useMemo<NonNullable<MuiPieChartProps['slots']>['tooltip']>(
    () =>
      ((props) => (
        <PieTooltipContent {...props} totalsByLabel={totalsByLabel} total={total} />
      )) as NonNullable<MuiPieChartProps['slots']>['tooltip'],
    [total, totalsByLabel],
  );
  const chartSx: MuiPieChartProps['sx'] = {
    ...{
      '& .MuiChartsTooltip-root': {
        pointerEvents: 'none',
      },
      '& .MuiPieArc-root': {
        stroke: theme.palette.background.paper,
        strokeWidth: 1,
      },
    },
    ...(getChartTooltipSx(theme) as object),
    ...(showLegend ? {} : { '& .MuiChartsLegend-root': { display: 'none' } }),
  };

  return (
    <Box sx={{ width: '100%', height }}>
      <MuiPieChart
        series={chartSeries}
        height={height}
        margin={CHART_MARGIN_NONE}
        slots={{
          tooltip: tooltipSlot,
        }}
        slotProps={PIE_TOOLTIP_SLOT_PROPS}
        sx={chartSx}
      />
      {showLegend ? (
        <Box sx={{ display: 'grid', gap: s('xs'), marginTop: s('sm') }}>
          {data.map((entry, index) => (
            <Box key={entry.name} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <Box
                  sx={{
                    width: s('sm'),
                    height: s('sm'),
                    backgroundColor: entry.color ?? pieChartPalette[index % pieChartPalette.length],
                  }}
                />
                <Text role="body">{entry.name}</Text>
              </Box>
              <Text role="bodyMuted">{entry.value}</Text>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
